/**
 * Auto-Save Hook
 *
 * Manages automatic saving of canvas to Firestore with:
 * - 10-second interval
 * - Debouncing (only save if changed)
 * - Error handling
 * - Retry logic
 * - Loading state
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import type { Project, FabricCanvasData } from '@canvas/shared-types';
import { useCanvasStore } from '@/stores/canvasStore';
import { useProjectStore } from '@/stores/projectStore';
import { useFirestore } from '@/hooks/useFirestore';
import { ensurePagesMatchCanvas } from '@/lib/projectPages';
import { isPreviewActive } from '@/lib/video/previewLock';

export interface UseAutoSaveOptions {
  /**
   * Interval between saves in milliseconds (default: 10000)
   */
  interval?: number;

  /**
   * Whether to generate thumbnail (slower but nice preview)
   */
  generateThumbnail?: boolean;

  /**
   * Max number of retries on failure
   */
  maxRetries?: number;

  /**
   * Callback when save succeeds
   */
  onSaveSuccess?: () => void;

  /**
   * Callback when save fails
   */
  onSaveError?: (error: Error) => void;
}

export interface UseAutoSaveReturn {
  /**
   * Is currently saving to Firestore
   */
  isSaving: boolean;

  /**
   * Last save time or null if never saved
   */
  lastSaved: Date | null;

  /**
   * If last save failed, error message
   */
  saveError: string | null;

  /**
   * Number of failed attempts since last success
   */
  failedAttempts: number;

  /**
   * Manually trigger a save (for Ctrl+S, etc)
   */
  manualSave: () => Promise<boolean>;

  /**
   * Clear any error state
   */
  clearError: () => void;
}

const DEFAULT_INTERVAL = 10000; // 10 seconds
const DEFAULT_MAX_RETRIES = 3;

/**
 * Auto-Save Hook
 *
 * Automatically saves canvas to Firestore at regular intervals.
 * Implements smart debouncing to avoid unnecessary writes.
 *
 * @example
 * const { isSaving, lastSaved, saveError, manualSave } = useAutoSave();
 *
 * return (
 *   <>
 *     <SaveIndicator isSaving={isSaving} lastSaved={lastSaved} error={saveError} />
 *     <button onClick={manualSave}>Save Now</button>
 *   </>
 * );
 */
export function useAutoSave(options: UseAutoSaveOptions = {}): UseAutoSaveReturn {
  const {
    interval = DEFAULT_INTERVAL,
    generateThumbnail = false,
    maxRetries = DEFAULT_MAX_RETRIES,
    onSaveSuccess,
    onSaveError,
  } = options;

  // State
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [failedAttempts, setFailedAttempts] = useState(0);

  // Refs - use refs to avoid dependencies
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastSaveTimeRef = useRef<number>(0);
  const lastCanvasHashRef = useRef<string>('');

  // Stores and hooks
  const markDirty = useCanvasStore((s) => s.markDirty);
  const {
    currentProject,
    autoSaveEnabled,
    saveProject,
  } = useProjectStore();
  const { saveProject: saveToFirestore } = useFirestore();

  /**
   * Generate canvas thumbnail for preview
   * Called only if generateThumbnail is true
   */
  const generateCanvasThumbnail = useCallback(
    (fabricCanvasData: FabricCanvasData): string | undefined => {
      if (!generateThumbnail || !canvasRef.current) {
        return undefined;
      }

      try {
        // Generate thumbnail (could be slow for large canvases)
        // In production, might want to use offscreen canvas or web workers
        return canvasRef.current.toDataURL('image/png', 0.8);
      } catch (error) {
        console.warn('Failed to generate thumbnail:', error);
        return undefined;
      }
    },
    [generateThumbnail]
  );

  /**
   * Create hash of canvas data to detect changes
   * Prevents saving identical data multiple times
   */
  const hashCanvasData = useCallback((data: FabricCanvasData | null): string => {
    if (!data) return '';

    try {
      // Simple hash based on object count and first few objects
      const objectCount = data.objects?.length ?? 0;
      const firstObject = JSON.stringify(data.objects?.[0] ?? {});
      // Timeline-only edits leave canvasData untouched; include them explicitly.
      const videoStamp = useProjectStore.getState().currentProject?.video?.updatedAt ?? 0;

      return `${objectCount}:${firstObject.substring(0, 50)}:${videoStamp}`;
    } catch (error) {
      // If hashing fails, return empty to force save
      return '';
    }
  }, []);

  /**
   * Perform the actual save operation
   * Handles retries and error reporting
   */
  const performSave = useCallback(
    async (isRetry = false, force = false): Promise<boolean> => {
      const latestCanvas = useCanvasStore.getState().canvasData;
      const latestProject = useProjectStore.getState().currentProject;
      const dirty = useCanvasStore.getState().isDirty;

      // Safety checks
      if (!latestProject || !latestCanvas) {
        return false;
      }

      // Never persist while the live canvas shows animated state; the next tick retries.
      if (isPreviewActive()) {
        return false;
      }

      const canvasHash = hashCanvasData(latestCanvas);
      const changed = canvasHash !== lastCanvasHashRef.current;

      // Don't save if nothing changed (manual/force always writes)
      if (!force && !isRetry && !changed && !dirty) {
        return true;
      }

      // Don't save too frequently (still leave dirty so the next tick retries)
      const now = Date.now();
      if (!force && !isRetry && now - lastSaveTimeRef.current < interval / 2) {
        return false;
      }

      setIsSaving(true);
      setSaveError(null);

      try {
        // Generate thumbnail if enabled
        const thumbnail = generateCanvasThumbnail(latestCanvas);

        // Keep pages[active] in sync with live canvas — otherwise reopen loads a blank page
        const synced = ensurePagesMatchCanvas(latestProject, latestCanvas);
        useProjectStore.getState().updateProject({
          ...latestProject,
          canvasData: latestCanvas,
          pages: synced.pages,
          activePageId: synced.activePageId,
        });

        // Update project in store (returns false on failure — does not throw)
        const ok = await saveProject(
          {
            canvasData: latestCanvas,
            thumbnail,
            pages: synced.pages,
            activePageId: synced.activePageId,
          },
          async (project: Project) => {
            // Save to Firestore
            await saveToFirestore(project);
          }
        );

        if (!ok) {
          const storeError = useProjectStore.getState().error;
          throw new Error(storeError || 'Failed to save project');
        }

        // Success — only then remember the hash so a skipped/failed save can retry
        setLastSaved(new Date());
        lastSaveTimeRef.current = now;
        lastCanvasHashRef.current = canvasHash;
        setIsSaving(false);
        setFailedAttempts(0);
        markDirty(false);

        onSaveSuccess?.();
        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        // Retry logic
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);

        if (newAttempts < maxRetries) {
          console.warn(`Auto-save failed, retrying... (${newAttempts}/${maxRetries})`, error);
          setSaveError(`Save failed, retrying... (${newAttempts}/${maxRetries})`);

          // Wait before retry (exponential backoff)
          const waitTime = Math.min(1000 * Math.pow(2, newAttempts), 5000);
          setTimeout(() => performSave(true, force), waitTime);
        } else {
          // Max retries exceeded
          console.error('Auto-save failed after max retries:', error);
          setSaveError(`Failed to save: ${errorMessage}`);
          setIsSaving(false);

          onSaveError?.(error instanceof Error ? error : new Error(errorMessage));
        }

        return false;
      }
    },
    [
      failedAttempts,
      saveProject,
      saveToFirestore,
      generateCanvasThumbnail,
      hashCanvasData,
      interval,
      maxRetries,
      markDirty,
      onSaveSuccess,
      onSaveError,
    ]
  );

  /**
   * Manual save trigger (for Ctrl+S, save button, close, etc)
   */
  const manualSave = useCallback(async (): Promise<boolean> => {
    return await performSave(false, true);
  }, [performSave]);

  /**
   * Setup auto-save interval
   */
  useEffect(() => {
    // Only setup if auto-save enabled and project loaded
    if (!autoSaveEnabled || !currentProject) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Clear existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Setup new timer
    timerRef.current = setInterval(() => {
      performSave(false);
    }, interval);

    // Cleanup
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [autoSaveEnabled, currentProject, interval, performSave]);

  /**
   * When a project is opened, treat loaded data as already saved so we don't
   * immediately rewrite Firestore with the same (or briefly empty) canvas.
   */
  useEffect(() => {
    if (!currentProject) return;
    const data = useCanvasStore.getState().canvasData;
    lastCanvasHashRef.current = hashCanvasData(data);
    lastSaveTimeRef.current = Date.now();
    setLastSaved(
      currentProject.lastModifiedAt instanceof Date
        ? currentProject.lastModifiedAt
        : new Date(currentProject.lastModifiedAt)
    );
    setSaveError(null);
    setFailedAttempts(0);
    markDirty(false);
  }, [currentProject?.projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Get canvas element from DOM for thumbnail generation
   */
  useEffect(() => {
    const canvas = document.querySelector('canvas') as HTMLCanvasElement;
    if (canvas) {
      canvasRef.current = canvas;
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setSaveError(null);
  }, []);

  return {
    isSaving,
    lastSaved,
    saveError,
    failedAttempts,
    manualSave,
    clearError,
  };
}
